import { useState, useRef, useEffect, useMemo } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface MultiSelectProps {
  value: string[];
  options: MultiSelectOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  searchable?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  id?: string;
  allLabel?: string;
}

export function MultiSelect({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  icon,
  searchable = false,
  size = 'md',
  className = '',
  id,
  allLabel,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }, [options, search]);

  const sizeClasses = size === 'sm'
    ? 'pl-2.5 pr-2 py-1.5 text-sm min-w-[100px]'
    : 'pl-3 pr-2.5 py-2 text-sm min-w-[140px]';

  const hasValue = value.length > 0;
  const summary = !hasValue
    ? (allLabel || placeholder)
    : value.length === 1
      ? (options.find((o) => o.value === value[0])?.label || value[0])
      : `${value.length} selected`;

  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  function clearAll(e: React.MouseEvent) {
    e.stopPropagation();
    onChange([]);
  }

  return (
    <div ref={ref} className={`relative ${className}`} id={id}>
      <button
        type="button"
        onClick={() => { setOpen(!open); if (open) setSearch(''); }}
        className={`flex items-center gap-2 ${sizeClasses} rounded-lg font-medium border transition-all duration-150 w-full ${
          open
            ? 'border-primary ring-2 ring-primary/40 bg-surface shadow-sm'
            : hasValue
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-border bg-surface text-content-muted hover:border-border-strong hover:shadow-sm'
        }`}
      >
        {icon && <span className="text-content-subtle flex-shrink-0">{icon}</span>}
        <span className={`truncate flex-1 text-left ${!hasValue ? 'text-content-subtle' : ''}`}>
          {summary}
        </span>
        {hasValue && (
          <span
            role="button"
            tabIndex={0}
            onClick={clearAll}
            className="flex-shrink-0 text-primary hover:text-primary"
            title="Clear"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        )}
        <svg
          className={`w-4 h-4 text-content-subtle flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1.5 w-full min-w-[200px] bg-surface border border-border rounded-xl shadow-lg overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-border">
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-surface-muted border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary/40 placeholder:text-content-subtle"
                />
              </div>
            </div>
          )}
          {hasValue && (
            <div className="px-3 py-1.5 border-b border-border flex items-center justify-between">
              <span className="text-xs text-content-subtle">{value.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-primary hover:text-primary font-medium"
              >
                Clear
              </button>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-content-subtle">No results</div>
            ) : (
              filtered.map((opt) => {
                const checked = value.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggle(opt.value)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                      checked ? 'bg-primary/10 text-primary font-medium' : 'text-content-muted hover:bg-surface-muted'
                    }`}
                  >
                    <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                      checked ? 'bg-purple-500 border-primary' : 'border-border-strong bg-surface'
                    }`}>
                      {checked && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    {opt.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                    <span className="truncate">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
